import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Section } from "../components/ui/section";
import { PageHeader } from "../components/ui/page-header";
import { CurrencyCombobox } from "../components/CurrencyCombobox";
import {
  LanguageCombobox,
  type LanguageCode,
} from "../components/LanguageCombobox";
import { useCreateGroup } from "../api/hooks";
import { DEFAULT_GROUP_LANGUAGE } from "@repo/api-schema";
import type { CreateGroupRequest } from "../api/types";

export function CreateGroupPage(): ReactNode {
  const navigate = useNavigate();
  const createGroup = useCreateGroup();
  const [label, setLabel] = useState("");
  const [currency, setCurrency] =
    useState<CreateGroupRequest["currency"]>("USD");
  const [language, setLanguage] = useState<LanguageCode>(
    DEFAULT_GROUP_LANGUAGE as LanguageCode,
  );

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const created = await createGroup.mutateAsync({
      label,
      currency,
      language,
    });
    navigate(`/groups/${created.id}`);
  }

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(-1) }}
        title="Create a group"
        description="Groups are how you split shared expenses with the same set of people."
      />
      <Section
        title="Details"
        description="Name your group and pick its base currency."
      >
        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="label">Name</Label>
            <Input
              id="label"
              placeholder="Mountain Trip"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              minLength={1}
              maxLength={120}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <CurrencyCombobox
              id="currency"
              value={currency}
              onChange={setCurrency}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="language">Receipt language</Label>
            <LanguageCombobox
              id="language"
              value={language}
              onChange={setLanguage}
            />
            <p className="text-label-sm text-on-surface-variant">
              Scanned receipts in another language are translated into this one.
            </p>
          </div>
          {createGroup.error ? (
            <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
              Failed to create group. Please try again.
            </div>
          ) : null}
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={createGroup.isPending}>
              {createGroup.isPending ? "Creating…" : "Create group"}
            </Button>
          </div>
        </form>
      </Section>
    </div>
  );
}
